/**
 * Shared style tokens + tiny helpers used across the new DevConsole
 * sections. Inline styles only — the rest of the panel already commits to
 * inline-style theming, no Tailwind, no CSS modules.
 */
import React from "react";
import { BN } from "@coral-xyz/anchor";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";

export const colors = {
  bg: "#08080d",
  bgRaised: "#0e0e15",
  bgHeader: "#11111a",
  border: "#1c1c26",
  borderStrong: "#2a2a36",
  text: "#e8e8ef",
  textMute: "#9aa0b4",
  textFaint: "#5a5a72",
  mono: "#cfd2dc",
  accent: "#4a8eff",
  ok: "#7af09e",
  fail: "#f47a8e",
  warn: "#f5c674",
  running: "#7ab0ff",
} as const;

export const styles = {
  sectionHeader: {
    padding: "10px 14px",
    background: colors.bgHeader,
    borderBottom: `1px solid ${colors.border}`,
    display: "flex",
    gap: 10,
    alignItems: "baseline",
    justifyContent: "space-between",
    flex: "0 0 auto",
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.text,
    letterSpacing: 0.4,
  } as React.CSSProperties,
  sectionSubtitle: {
    fontSize: 11,
    color: colors.textMute,
  } as React.CSSProperties,
  card: {
    padding: 12,
    background: colors.bgRaised,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  } as React.CSSProperties,
  row: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  } as React.CSSProperties,
  inputStyle: {
    flex: 1,
    minWidth: 0,
    padding: "5px 8px",
    background: colors.bg,
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: 4,
    color: colors.text,
    fontSize: 11,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  } as React.CSSProperties,
  label: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 1.2,
    color: colors.textFaint,
    textTransform: "uppercase",
  } as React.CSSProperties,
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    color: colors.mono,
    fontSize: 11,
    wordBreak: "break-all",
  } as React.CSSProperties,
};

export function btnStyle(opts?: {
  tone?: "default" | "primary" | "danger" | "ghost";
  disabled?: boolean;
  small?: boolean;
}): React.CSSProperties {
  const tone = opts?.tone ?? "default";
  const palette = {
    default: { bg: "#3a3a4e", border: "#2a2a36", color: colors.text },
    primary: { bg: "#1c3a5e", border: "#2a4a7a", color: "#cfe3ff" },
    danger: { bg: "#3a1c26", border: "#5a2a36", color: "#f47a8e" },
    ghost: { bg: "transparent", border: "#2a2a36", color: colors.textMute },
  }[tone];
  return {
    padding: opts?.small ? "3px 8px" : "5px 12px",
    background: opts?.disabled ? "#22222e" : palette.bg,
    color: opts?.disabled ? "#5a5a72" : palette.color,
    border: `1px solid ${palette.border}`,
    borderRadius: 4,
    fontSize: opts?.small ? 10 : 11,
    fontWeight: 600,
    cursor: opts?.disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap",
  };
}

export function fmtSol(lamports: number | null | undefined | bigint | BN): string {
  if (lamports === null || lamports === undefined) return "—";
  let n: bigint;
  if (typeof lamports === "bigint") n = lamports;
  else if (typeof lamports === "number") n = BigInt(Math.floor(lamports));
  else n = BigInt(lamports.toString());
  const whole = n / BigInt(LAMPORTS_PER_SOL);
  const frac = n % BigInt(LAMPORTS_PER_SOL);
  return `${whole}.${frac.toString().padStart(9, "0").slice(0, 4)}`;
}

export function shortKey(pk: string | null | undefined): string {
  if (!pk) return "—";
  if (pk.length <= 12) return pk;
  return `${pk.slice(0, 4)}…${pk.slice(-4)}`;
}

export function fmtRelTime(ts: number): string {
  const delta = Date.now() - ts;
  if (delta < 1000) return "now";
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3600_000) return `${Math.floor(delta / 60_000)}m ago`;
  return `${Math.floor(delta / 3600_000)}h ago`;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to legacy path */
    }
  }
  if (typeof document === "undefined") return false;
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
